import { Modal, Radio, Typography } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';

import { Button } from '~/components';
import DatePicker from '~/components/DatePicker';
import colors from '~/constants/colors';
import { useDirection } from '~/hooks/i18nHooks';

interface ExportLeadsModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (dateRange: {
    fromDate?: string;
    toDate?: string;
    type: 'lastWeek' | 'lastMonth' | 'custom';
  }) => void;
}

type DateRangeType = 'lastWeek' | 'lastMonth' | 'custom';

const ExportLeadsModal: React.FC<ExportLeadsModalProps> = ({ open, onClose, onExport }) => {
  const { t } = useTranslation('sales');
  const dir = useDirection();
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('lastWeek');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);

  const handleExport = () => {
    setLoading(true);

    let exportData: { fromDate: string; toDate: string; type: DateRangeType } = {
      type: dateRangeType,
      fromDate: '',
      toDate: '',
    };

    if (dateRangeType === 'custom') {
      exportData.fromDate = fromDate ? fromDate.format('YYYY-MM-DD') : '';
      exportData.toDate = toDate ? toDate.format('YYYY-MM-DD') : '';
    } else if (dateRangeType === 'lastWeek') {
      exportData.fromDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
      exportData.toDate = dayjs().format('YYYY-MM-DD');
    } else if (dateRangeType === 'lastMonth') {
      exportData.fromDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
      exportData.toDate = dayjs().format('YYYY-MM-DD');
    }

    onExport(exportData);
    setLoading(false);
    handleClose();
  };

  const handleClose = () => {
    setDateRangeType('lastWeek');
    setFromDate(null);
    setToDate(null);
    onClose();
  };

  const isExportDisabled = dateRangeType === 'custom' && (!fromDate || !toDate);

  return (
    <StyledModal open={open} onCancel={handleClose} footer={null} width={500} centered closable>
      <ModalContent>
        <Typography.Title level={5}>{t('Specify the Capturing Dates Required')}</Typography.Title>

        <StyledRadioGroup value={dateRangeType} onChange={(e) => setDateRangeType(e.target.value)}>
          <Radio value="lastWeek">{t('Last Week')}</Radio>
          <Radio value="lastMonth">{t('Last Month')}</Radio>
          <Radio value="custom">{t('Custom')}</Radio>
        </StyledRadioGroup>

        {dateRangeType === 'custom' && (
          <DateRangeContainer>
            <DatePickerWrapper>
              <Typography.Text>{t('From')}</Typography.Text>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                placeholder={t('Select date')}
                disabledDate={(current: Dayjs | null) => {
                  if (!current) return false;
                  if (toDate) return current.isAfter(toDate);
                  return current.isAfter(dayjs());
                }}
              />
            </DatePickerWrapper>

            <DatePickerWrapper>
              <Typography.Text>{t('To')}</Typography.Text>
              <DatePicker
                value={toDate}
                onChange={setToDate}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                placeholder={t('Select date')}
                disabledDate={(current: Dayjs | null) => {
                  if (!current) return false;
                  if (fromDate) return current.isBefore(fromDate);
                  return current.isAfter(dayjs());
                }}
              />
            </DatePickerWrapper>
          </DateRangeContainer>
        )}

        <ExportButtonContainer>
          <Button
            id="exportLeadsConfirmButton"
            type="primary"
            onClick={handleExport}
            loading={loading}
            disabled={isExportDisabled}
          >
            {t('Export')}
          </Button>
        </ExportButtonContainer>
      </ModalContent>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    padding: 24px;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
  }

  .ant-modal-close-x {
    width: 32px;
    height: 32px;
    line-height: 32px;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .ant-typography-title {
    margin: 0;
    color: ${colors.blue[1100]};
    text-align: center;
    font-size: 16px;
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  justify-content: center;
  gap: 24px;

  .ant-radio-wrapper {
    font-size: 14px;
    color: ${colors.textColor};

    .ant-radio-checked .ant-radio-inner {
      border-color: ${colors.blue[500]};
      background-color: ${colors.blue[500]};
    }

    &:hover .ant-radio-inner {
      border-color: ${colors.blue[500]};
    }
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: flex-end;
`;

const DatePickerWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-typography {
    font-size: 14px;
    color: ${colors.textColor};
    font-weight: 500;
  }

  .ant-picker {
    width: 100%;
    border-radius: 4px;
    border-color: ${colors.borderColorBase};

    &:hover {
      border-color: ${colors.blue[500]};
    }

    &.ant-picker-focused {
      border-color: ${colors.blue[500]};
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
  }
`;

const ExportButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;

  button {
    min-width: 120px;
  }
`;

const ExportInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid ${colors.borderColorBase};

  .ant-typography {
    font-size: 12px;
  }
`;

export default ExportLeadsModal;
